<script setup>
import { computed } from 'vue'

import EntityLockAvatarTemplate from '@/components/shared/EntityLockAvatarTemplate.vue'
import { usePortalStore } from '@/stores/portal.store'

const props = defineProps({
  entityName: String,
  masterEntityId: Number,
  cellInfo: Object,
  grid: Object,
  entityLock: Object,
  buttons: {
    type: Array,
    default: () => [] // { icon, onClick, visibleFn }
  },
  isLockedAvailable: {
    type: Boolean,
    default: false
  }
})

const portalStore = usePortalStore()

const entityId = props.cellInfo.row?.data?.id

const effectiveMasterEntityId = computed(() => props.masterEntityId || entityId)

const isLockedByAnother = computed(() => {
  return props.entityLock?.isLocked(false, effectiveMasterEntityId.value)
})

const computedButtons = computed(() => {
  return props.buttons.map(btn => {
    let visible = true
    if (isLockedByAnother.value && btn.name !== 'view') {
      visible = false
    } else if (btn.visibleFn) {
      try {
        visible = btn.visibleFn({
          entityId: effectiveMasterEntityId.value,
          isReadonly: props.cellInfo?.row?.data?.is_portal || props.cellInfo?.row?.data?.worksheet?.id || props.cellInfo?.row?.data?.has_sale_document || (!!props.cellInfo?.row?.data?.electronic_invoice_progress_status && !portalStore.electronicInvoiceEditableFamilies.includes(portalStore.getElectronicInvoiceFamilyByProgressStatus(props.cellInfo?.row?.data?.electronic_invoice_progress_status))) || false,
          data: props.cellInfo?.row?.data,
        })
      } catch (e) {
        console.warn('visibleFn error:', e)
        visible = false
      }
    } else {
      visible = !isLockedByAnother.value
    }

    let tooltip = null
    if (btn.tooltipFn) {
      try {
        tooltip = btn.tooltipFn({
          entityId: effectiveMasterEntityId.value,
          data: props.cellInfo?.row?.data,
        })
      } catch (e) {
        console.warn('tooltipFn error:', e)
      }
    }

    return {
      ...btn,
      visible,
      tooltip
    }
  })
})
</script>

<template>
  <slot>
    <template
      v-for="(button, index) in computedButtons"
      :key="index"
    >
      <slot :name="`before-${button.name}`" />
      <a
        v-if="button.visible"
        href="#"
        :class="button.class || [
          'dx-link',
          `dx-link-${button.name}`,
          `dx-icon-${button.icon}`,
          'dx-link-icon'
        ]"
        :title="button.tooltip?.length ? null : (button.text || button.name)"
        :aria-label="button.text || button.name"
        @click.prevent="button.onClick({ grid: props.grid, entityId: entityId })"
      >
        <q-tooltip
          v-if="button.tooltip?.length"
          anchor="bottom middle"
          self="top middle"
          :delay="250"
          class="bg-white text-dark shadow-5"
        >
          <q-item-label
            v-if="button.tooltipTitle"
            class="text-bold"
          >
            {{ button.tooltipTitle }}
          </q-item-label>
          <q-separator
            v-if="button.tooltipTitle"
            spaced
          />
          <q-item-label
            v-for="(reason, reasonIndex) in button.tooltip"
            :key="reasonIndex"
            class="q-py-xs"
          >
            <q-icon name="mdi-circle-medium" />
            {{ reason }}
          </q-item-label>
        </q-tooltip>
      </a>
    </template>
    <slot name="after-actions" />
    <entity-lock-avatar-template
      v-if="isLockedAvailable && isLockedByAnother"
      class="dx-link dx-link-icon"
      :entity-name="props.entityName"
      :entity-id="entityId"
      :size="'16px'"
      @click="props.entityLock.unlockRequestConfirmation(entityId)"
    />
  </slot>
</template>
